import React, { Fragment } from 'react';
import { FormattedNumber } from 'react-intl';

import { useInvoiceBreakdownAnalytics } from './hooks/useInvoiceBreakdownAnalytics';

import InvoiceLineItems from 'components/Core/Invoice/InvoiceLineItems/InvoiceLineItems';
import DataRow from 'components/Shared/DataRow/DataRow';
import ExtendBtn from 'components/Shared/ExtendBtn/ExtendBtn';
import Hr from 'components/Shared/Hr/Hr';
import { useIsMobileScreen } from 'hooks/useIsMobile';
import { colors, fontSize } from 'styles/cp';
import { LineItem } from 'types/Sale';

export interface InvoiceBreakdownSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  invoiceAmount: number;
  currency?: string;
  invoiceLineItems?: LineItem[];
  subTotalAmount?: number;
  discountAmount?: number;
  shippingAmount?: number;
  invoiceTaxAmount?: number;
  hrColor?: string;
}

const InvoiceBreakdownSection: React.FC<InvoiceBreakdownSectionProps> = ({
  isExpanded,
  onToggle,
  invoiceAmount,
  currency,
  invoiceLineItems,
  subTotalAmount,
  discountAmount,
  shippingAmount,
  invoiceTaxAmount,
  hrColor,
}) => {
  const isMobile = useIsMobileScreen();
  const { handleToggleWithAnalytics } = useInvoiceBreakdownAnalytics({
    isExpanded,
    invoiceLineItems,
    onToggle,
  });
  return (
    <Fragment>
      {/* Total - positioned above breakdown button */}
      <Hr marginTop={10} marginBottom={10} borderColor={colors.offWhite} opacity={1} />
      <DataRow
        idValue="INVOICE_BREAKDOWN_PERSISTENT_TOTAL"
        defaultMessage="Total"
        bold={true}
        color={colors.black}
        value={<FormattedNumber value={invoiceAmount} style="currency" currency={currency} />}
      />

      {/* Breakdown button using DataRow pattern like existing expandables */}
      <Hr marginTop={10} marginBottom={10} borderColor={colors.offWhite} opacity={1} />
      <div className="breakdown-row-item">
        <div
          className="space-between flex-row breakdown-row-clickable"
          style={{ gap: 15 }}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Hide invoice breakdown' : 'Show invoice breakdown'}
          onClick={handleToggleWithAnalytics}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggleWithAnalytics();
            }
          }}
        >
          <span className="label left">
            <span className="borderBottom">Invoice breakdown</span>
          </span>
          {/* aria-hidden + tabIndex={-1}: outer div[role=button] is the AT-visible control; chevron is decorative */}
          <span className="label label-data right" aria-hidden="true">
            <ExtendBtn
              opened={isExpanded}
              handler={handleToggleWithAnalytics}
              margin="unset"
              ariaLabel={isExpanded ? 'Hide invoice breakdown' : 'Show invoice breakdown'}
              tabIndex={-1}
            />
          </span>
        </div>
      </div>

      {/* Expandable content with animation (non-mobile only) or conditional rendering (mobile) */}
      {isMobile ? (
        // Mobile: Simple conditional rendering (no animation)
        isExpanded &&
        invoiceLineItems &&
        hrColor && (
          <InvoiceLineItems
            invoiceLineItems={invoiceLineItems}
            currency={currency}
            subTotalAmount={subTotalAmount || 0}
            discountAmount={discountAmount || 0}
            shippingAmount={shippingAmount || 0}
            invoiceTaxAmount={invoiceTaxAmount || 0}
            hrColor={hrColor}
            showTopHr={false}
            showBottomTotal={false}
          />
        )
      ) : (
        // Non-mobile: Animated container (same as FAQ components)
        <div className="breakdown-content-container">
          {invoiceLineItems && hrColor && (
            <InvoiceLineItems
              invoiceLineItems={invoiceLineItems}
              currency={currency}
              subTotalAmount={subTotalAmount || 0}
              discountAmount={discountAmount || 0}
              shippingAmount={shippingAmount || 0}
              invoiceTaxAmount={invoiceTaxAmount || 0}
              hrColor={hrColor}
              showTopHr={false}
              showBottomTotal={false}
            />
          )}
        </div>
      )}

      <style jsx>{`
        .space-between {
          display: flex;
          justify-content: space-between;
        }

        .flex-row {
          flex-direction: row;
          margin: 6px 0px;
        }

        .label {
          cursor: text;
          font-size: ${fontSize.xs};
          color: ${colors.gray};
          display: flex;
          font-family: AvenirNextforINTUIT-Regular;
        }

        .left {
          text-align: left;
        }

        .right {
          text-align: right;
        }

        .label-data {
          font-family: AvenirNextforINTUIT-Medium;
          max-width: 170px;
        }

        .breakdown-row-clickable {
          cursor: pointer;
          width: 100%;
          margin-bottom: ${isExpanded ? '10px' : '-5px'};
          transition: margin-bottom 0.25s ease-in-out;
        }

        .breakdown-row-clickable:hover {
          opacity: 0.8;
        }

        .breakdown-row-clickable:focus {
          outline: none;
        }

        .breakdown-row-clickable:focus-visible {
          outline: none;
          box-shadow: 0px 0px 0px 1px white, 0px 0px 0px 4px ${colors.intuit_blue};
          border-radius: 2px;
        }

        .breakdown-row-clickable * {
          cursor: pointer;
        }

        .breakdown-row-item {
          display: block;
        }

        /* Smooth animation for breakdown content (non-mobile only) - matches FAQ component animation */
        .breakdown-content-container {
          max-height: ${isExpanded ? '3000px' : 0};
          transition: all 0.25s ease-in-out;
          overflow: ${isExpanded ? 'visible' : 'hidden'};
        }

        /* Ensure chevron has smooth rotation (matches FAQ ExtendBtn animation) */
        .breakdown-content-container :global(svg) {
          transition: all 0.15s linear;
        }
      `}</style>
    </Fragment>
  );
};

export default InvoiceBreakdownSection;
